import { SanitizedHtml } from '../components/SanitizedHtml';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { legalPagesApi, type DatenschutzPageContent } from '../services/legalPagesApi';
import { fallbackDatenschutzData } from '../data/legalPagesData';
import './DatenschutzPage.css';

const DatenschutzPage: React.FC = () => {
  const location = useLocation();
  const [datenschutzData, setDatenschutzData] = useState<DatenschutzPageContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Inhalte.')), 10000)
        );
        
        const apiPromise = legalPagesApi.getDatenschutzContent();
        
        const content = await Promise.race([apiPromise, timeoutPromise]) as DatenschutzPageContent;
        setDatenschutzData(content);
      } catch (err) {
        setDatenschutzData(fallbackDatenschutzData);
        console.error('Datenschutzerklärung konnte nicht geladen werden:', err);
        setError('Die Inhalte konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="datenschutz-loading">
        <div className="container">
          <h2 className="datenschutz-loading-text">Lade Inhalte...</h2>
        </div>
      </div>
    );
  }

  if (error || !datenschutzData) {
    return (
      <div className="datenschutz-loading">
        <div className="container">
          <h2 className="datenschutz-loading-text">Datenschutzerklärung</h2>
          <p>{error || 'Inhalte können nicht geladen werden'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="datenschutz-page">
      <div className="container">
        {/* Header */}
        <div className="datenschutz-header">
          <h1 className="datenschutz-title">{datenschutzData.title}</h1>
          <p className="datenschutz-updated">
            Letzte Aktualisierung: {new Date(datenschutzData.lastUpdated).toLocaleDateString('de-DE')}
          </p>
        </div>

        <div className="datenschutz-card">
          {/* Table of Contents */}
          <div className="datenschutz-toc">
            <h2 className="datenschutz-toc-title">Inhaltsverzeichnis</h2>
            <nav className="datenschutz-toc-nav">
              {datenschutzData.sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  className="datenschutz-toc-link"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          {/* Sections */}
          <div className="datenschutz-content">
            <div className="datenschutz-sections">
              {datenschutzData.sections
                .sort((a, b) => a.order - b.order)
                .map((section, index) => (
                <div key={index} id={`section-${index}`} className="datenschutz-section">
                  <h2 className="datenschutz-section-title">
                    {section.title}
                  </h2>
                  <SanitizedHtml className="datenschutz-section-content" content={section.content} format="markdown" />
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          {datenschutzData.contactInfo && (
            <div className="datenschutz-contact">
              <h2 className="datenschutz-contact-title">Kontakt zum Datenschutz</h2>
              
              <div className="datenschutz-contact-info">
                <h3 className="datenschutz-company-name">{datenschutzData.contactInfo.company}</h3>
                <div className="datenschutz-address">
                  {datenschutzData.contactInfo.address.map((line, index) => (
                    <span key={index} className="datenschutz-address-line">{line}</span>
                  ))}
                </div>
                
                <div className="datenschutz-contact-grid">
                  <div className="datenschutz-contact-item">
                    <span className="datenschutz-contact-label">E-Mail</span>
                    <a href={`mailto:${datenschutzData.contactInfo.email}`} className="datenschutz-contact-link">
                      {datenschutzData.contactInfo.email}
                    </a>
                  </div>
                  
                  {datenschutzData.contactInfo.phone && (
                    <div className="datenschutz-contact-item">
                      <span className="datenschutz-contact-label">Telefon</span>
                      <a href={`tel:${datenschutzData.contactInfo.phone}`} className="datenschutz-contact-link">
                        {datenschutzData.contactInfo.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="datenschutz-footer">
          Diese Datenschutzerklärung wurde zuletzt aktualisiert am: {new Date(datenschutzData.lastUpdated).toLocaleDateString('de-DE')}<br />
          Stand der Generierung: {new Date(datenschutzData.generatedAt).toLocaleDateString('de-DE')}
        </div>
      </div>
    </div>
  );
};

export default DatenschutzPage;
